import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiSun, FiMoon, FiGrid, FiChevronLeft, FiChevronRight, FiCalendar,
  FiPlus, FiTrash2, FiEdit2, FiCheck, FiX, FiLock, FiUnlock,
} from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';
import { useLocale } from '../context/LocaleContext';
import { useAuth } from '../context/AuthContext';
import { useModule } from '../context/ModuleContext';
import * as kassaService from '../services/kassaService';
import KassaGoldLedger from './KassaGoldLedger';
import KassaCloseModal from './KassaCloseModal';
import ReceiptPreview from '../components/ReceiptPreview';
import { getReceiptType } from '../config/receipts.config';
import '../gold-purchase.css';
import '../receipt-print.css';

/* =========================================================================
 * Helpers (pure)
 * ========================================================================= */

function money(n) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
}
function toISODate(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function addDays(iso, delta) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return toISODate(dt);
}
function prettyDate(iso, locale) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(locale === 'pt-BR' ? 'pt-BR' : 'en-US', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  });
}
function editVal(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) && n !== 0 ? String(v) : '';
}
const CURR = ['usd', 'srd', 'eur'];
function sumType(txs, type) {
  return txs.reduce((acc, tx) => {
    if (tx.type !== type) return acc;
    acc.usd += parseFloat(tx.amount_usd) || 0;
    acc.srd += parseFloat(tx.amount_srd) || 0;
    acc.eur += parseFloat(tx.amount_eur) || 0;
    return acc;
  }, { usd: 0, srd: 0, eur: 0 });
}

/* =========================================================================
 * Component
 * ========================================================================= */

export default function KassaDashboard() {
  const { user, logout } = useAuth();
  const { t, locale, setLocale } = useLocale();
  const { theme, toggleTheme } = useTheme();
  const { goToPicker } = useModule();

  const [date, setDate] = useState(() => toISODate(new Date()));
  const [view, setView] = useState('kassa'); // 'kassa' | 'gold'

  const [transactions, setTransactions] = useState([]);
  const [openingSet, setOpeningSet] = useState(false);
  const [opening, setOpening] = useState({ usd: '', srd: '', eur: '' });
  const [goldPaid, setGoldPaid] = useState({ usd: 0, srd: 0, eur: 0 });
  const [closed, setClosed] = useState(false);

  // Register draft row.
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  // Print (delegated from the gold ledger).
  const [printRow, setPrintRow] = useState(null);
  const [printing, setPrinting] = useState(false);

  // Close-register ("Kassa afsluiten") modal.
  const [showClose, setShowClose] = useState(false);

  // ---- Live sync ----
  const lastVersion = useRef('');
  const pendingSync = useRef(false);
  const dateRef = useRef(date); dateRef.current = date;
  const draftRef = useRef(draft); draftRef.current = draft;
  const openingBusy = useRef(false);
  const printingRef = useRef(printing); printingRef.current = printing;

  const applyDay = (data) => {
    setOpeningSet(!!data.opening_set);
    setClosed(!!data.closed);
    setTransactions(data.transactions || []);
    setGoldPaid(data.gold_paid || { usd: 0, srd: 0, eur: 0 });
    if (data.opening) {
      setOpening({ usd: String(data.opening.usd ?? ''), srd: String(data.opening.srd ?? ''), eur: String(data.opening.eur ?? '') });
    } else {
      setOpening({ usd: '', srd: '', eur: '' });
    }
  };

  const loadDay = async (iso = dateRef.current) => {
    const res = await kassaService.getDay(iso);
    if (res.success && res.data) {
      applyDay(res.data);
      const v = await kassaService.getDayVersion(iso);
      if (v.success && v.data) lastVersion.current = v.data.version;
    }
  };

  useEffect(() => {
    loadDay(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Poll kassa/day-version (folds in gold changes) every 4s.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled || document.visibilityState !== 'visible' || printingRef.current) return;
      const iso = dateRef.current;
      const v = await kassaService.getDayVersion(iso);
      if (cancelled || !v.success || !v.data) return;
      if (v.data.version === lastVersion.current) return;
      if (draftRef.current || openingBusy.current) pendingSync.current = true;
      else await loadDay(iso);
    };
    const id = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!draft && pendingSync.current) {
      pendingSync.current = false;
      loadDay(dateRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  /* ---- Saldo (computed live: opening + received − payments − gold paid) ---- */
  const received = useMemo(() => sumType(transactions, 'recebido'), [transactions]);
  const paid = useMemo(() => sumType(transactions, 'pagamento'), [transactions]);
  const base = { usd: parseFloat(opening.usd) || 0, srd: parseFloat(opening.srd) || 0, eur: parseFloat(opening.eur) || 0 };
  const saldo = {
    usd: base.usd + received.usd - paid.usd - (goldPaid.usd || 0),
    srd: base.srd + received.srd - paid.srd - (goldPaid.srd || 0),
    eur: base.eur + received.eur - paid.eur - (goldPaid.eur || 0),
  };
  const hasGoldPaid = (goldPaid.usd || 0) !== 0 || (goldPaid.srd || 0) !== 0 || (goldPaid.eur || 0) !== 0;

  /* ---- Opening balance ---- */
  const commitOpening = async (next) => {
    openingBusy.current = false;
    const res = await kassaService.setOpening(date, {
      usd: parseFloat(next.usd) || 0, srd: parseFloat(next.srd) || 0, eur: parseFloat(next.eur) || 0,
    });
    if (res.success) { setOpeningSet(true); lastVersion.current = ''; }
    else alert(res.message || 'Failed to save opening balances');
  };
  const setOpeningField = (c, val) => setOpening((o) => ({ ...o, [c]: val }));

  /* ---- Register draft ---- */
  const startAdd = () => setDraft({ id: null, type: 'recebido', description: '', usd: '', srd: '', eur: '' });
  const startEdit = (tx) => setDraft({ id: tx.id, type: tx.type, description: tx.description || '', usd: tx.amount_usd, srd: tx.amount_srd, eur: tx.amount_eur });
  const setDraftField = (key, val) => setDraft((d) => ({ ...d, [key]: val }));
  const cancelDraft = () => setDraft(null);

  const confirmDraft = async () => {
    if (!draft || saving) return;
    const usd = parseFloat(draft.usd) || 0;
    const srd = parseFloat(draft.srd) || 0;
    const eur = parseFloat(draft.eur) || 0;
    if (usd === 0 && srd === 0 && eur === 0) { alert(t('kassa_need_amount')); return; }
    setSaving(true);
    try {
      let res;
      if (draft.id === null) {
        res = await kassaService.addTransaction(date, draft.type, draft.description.trim(), { usd, srd, eur });
      } else {
        res = await kassaService.updateTransaction(draft.id, { type: draft.type, description: draft.description.trim(), usd, srd, eur });
      }
      if (res.success) { setDraft(null); await loadDay(date); }
      else alert(res.message || 'Failed to save transaction');
    } finally {
      setSaving(false);
    }
  };

  const deleteTx = async (id) => {
    if (!window.confirm(t('delete_tx_confirm'))) return;
    const res = await kassaService.deleteTransaction(id);
    if (res.success) await loadDay(date);
    else alert(res.message || 'Failed to delete transaction');
  };

  const handleReopen = async () => {
    if (!window.confirm(t('kassa_reopen_confirm'))) return;
    const res = await kassaService.reopenClosing(date);
    if (res.success) { lastVersion.current = ''; await loadDay(date); }
    else alert(res.message || 'Failed to reopen');
  };

  /* ---- Date nav ---- */
  const goPrev = () => setDate((d) => addDays(d, -1));
  const goNext = () => setDate((d) => addDays(d, 1));
  const goToday = () => setDate(toISODate(new Date()));
  const isToday = date === toISODate(new Date());

  /* ---- Print (gold receipts) ---- */
  const receiptType = useMemo(() => getReceiptType('compra_ouro'), []);
  const printValues = useMemo(() => {
    if (!printRow) return {};
    return {
      note_number: printRow.note_number,
      peso: printRow.grams,
      preco: printRow.price_per_gram,
      total: printRow.total_usd,
      pago_usd: printRow.paid_usd,
      pago_srd: printRow.paid_srd,
      pago_eur: printRow.paid_eur,
    };
  }, [printRow]);

  const handlePrint = (row) => {
    setPrintRow(row);
    setPrinting(true);
    setTimeout(() => {
      const done = () => { setPrinting(false); window.removeEventListener('afterprint', done); };
      window.addEventListener('afterprint', done);
      window.print();
      setTimeout(done, 1500);
    }, 150);
  };

  // Print mode: render only the receipt (receipt-print.css handles print sizing).
  if (printing) {
    return (
      <div className="gp-print-only">
        <ReceiptPreview type={receiptType} values={printValues} currency="USD" t={t} />
      </div>
    );
  }

  return (
    <div className="layout">
      <header className="app-header">
        <div className="brand">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="Logo" />
          <h1 className="app-title">{t('brand_title')}</h1>
        </div>
        <div className="app-actions">
          <button className="btn btn-ghost btn-icon" onClick={goToPicker} title={t('back_to_modules')} aria-label={t('back_to_modules')}>
            <FiGrid />
          </button>
          <button className="btn btn-ghost btn-icon" onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} aria-label="Toggle theme">
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
          <select className="lang-select" value={locale} onChange={(e) => setLocale(e.target.value)} aria-label="Language" title="Language">
            <option value="en">English</option>
            <option value="pt-BR">Português (BR)</option>
          </select>
          <span className="muted">{t('signed_in_as')} {user?.username}</span>
          <button className="btn btn-ghost" onClick={logout}>{t('logout')}</button>
        </div>
      </header>

      <main className="content gp-content">
        {/* Date navigation + view tabs */}
        <div className="gp-datebar">
          <button className="btn btn-ghost btn-icon" onClick={goPrev} title={t('prev_day')}><FiChevronLeft /></button>
          <div className="gp-date-display">
            <FiCalendar />
            <span className="gp-date-text">{prettyDate(date, locale)}</span>
            <input type="date" className="gp-date-input" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
          </div>
          <button className="btn btn-ghost btn-icon" onClick={goNext} title={t('next_day')}><FiChevronRight /></button>
          {!isToday && <button className="btn btn-ghost btn-sm" onClick={goToday}>{t('today')}</button>}

          {closed ? (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span className="kassa-closed-badge"><FiLock /> {t('kassa_closed')}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowClose(true)}>{t('kassa_close_title')}</button>
              <button className="btn btn-primary btn-sm" onClick={handleReopen}><FiUnlock style={{ marginRight: '6px' }} /> {t('kassa_unlock')}</button>
            </div>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={() => setShowClose(true)} style={{ marginLeft: 'auto' }}>
              {t('kassa_close_title')}
            </button>
          )}

          <div className="segmented kassa-view-switch" role="tablist" aria-label="View">
            <button type="button" className={'segmented-item' + (view === 'kassa' ? ' active' : '')} onClick={() => setView('kassa')} role="tab" aria-selected={view === 'kassa'}>
              {t('module_kassa')}
            </button>
            <button type="button" className={'segmented-item' + (view === 'gold' ? ' active' : '')} onClick={() => setView('gold')} role="tab" aria-selected={view === 'gold'}>
              {t('kassa_gold_tab')}
            </button>
          </div>
        </div>

        {/* Shared saldo bar (visible in both views) */}
        <div className="gp-balance-bar">
          <div className="gp-bal-group">
            <span className="gp-bal-label">
              {t('kassa_opening')}
              {!openingSet && <span className="muted small"> ({t('kassa_not_set')})</span>}
            </span>
            <div className="gp-bal-inline">
              {CURR.map((c) => (
                <span className="gp-bal-chip" key={c} style={{ gap: '4px' }}>
                  <span className="gp-bal-code">{c.toUpperCase()}</span>
                  <input
                    className="gp-cell" style={{ width: '92px' }}
                    type="number" step="0.01" inputMode="decimal" placeholder="0.00"
                    value={opening[c]}
                    disabled={closed}
                    onFocus={() => { openingBusy.current = true; }}
                    onChange={(e) => setOpeningField(c, e.target.value)}
                    onBlur={() => commitOpening({ ...opening, [c]: opening[c] })}
                  />
                </span>
              ))}
            </div>
          </div>

          <span className="gp-bal-sep gp-bal-sep-plus">+</span>
          <div className="gp-bal-group">
            <span className="gp-bal-label">{t('kassa_recebido')}</span>
            <BalInline bal={received} tone="pos" />
          </div>

          <span className="gp-bal-sep">−</span>
          <div className="gp-bal-group">
            <span className="gp-bal-label">{t('kassa_pagamento')}</span>
            <BalInline bal={paid} tone="neg" />
          </div>

          {hasGoldPaid && (
            <>
              <span className="gp-bal-sep">−</span>
              <div className="gp-bal-group">
                <span className="gp-bal-label">{t('kassa_gold_paid')}</span>
                <BalInline bal={goldPaid} tone="neg" />
              </div>
            </>
          )}

          <span className="gp-bal-sep">=</span>
          <div className="gp-bal-group gp-bal-group-gold">
            <span className="gp-bal-label">{t('kassa_saldo')}</span>
            <BalInline bal={saldo} strong />
          </div>
        </div>

        {/* Body: register OR gold ledger */}
        {view === 'kassa' ? (
          <div className="gp-table-wrap">
            <table className="gp-table">
              <thead>
                <tr>
                  <th style={{ width: '130px' }}>{t('type')}</th>
                  <th>{t('description')}</th>
                  <th className="gp-num gp-paid-col">USD</th>
                  <th className="gp-num gp-paid-col">SRD</th>
                  <th className="gp-num gp-paid-col">EUR</th>
                  <th className="gp-actions-col"></th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 && !draft && (
                  <tr><td colSpan={6} className="gp-empty muted">{t('no_transactions')}</td></tr>
                )}
                {transactions.map((tx) => {
                  if (draft && draft.id === tx.id) {
                    return <RegisterDraftRow key={tx.id} draft={draft} saving={saving} onField={setDraftField} onConfirm={confirmDraft} onCancel={cancelDraft} t={t} />;
                  }
                  const isRec = tx.type === 'recebido';
                  const sign = isRec ? '+' : '−';
                  return (
                    <tr key={tx.id}>
                      <td>
                        <span className={'kassa-pill ' + (isRec ? 'pos' : 'neg')}>{isRec ? t('kassa_recebido') : t('kassa_pagamento')}</span>
                      </td>
                      <td className="kassa-desc">{tx.description || '—'}</td>
                      {CURR.map((c) => {
                        const v = parseFloat(tx['amount_' + c]) || 0;
                        return (
                          <td key={c} className={'gp-num gp-paid-col ' + (v === 0 ? 'gp-ro' : (isRec ? 'gp-gold-pos' : 'gp-gold-neg'))}>
                            {v === 0 ? '—' : `${sign} ${money(v)}`}
                          </td>
                        );
                      })}
                      <td className="gp-actions-col">
                        <div className="gp-row-actions">
                          <button className="btn btn-ghost btn-icon" title={t('edit')} onClick={() => startEdit(tx)} disabled={!!draft || closed}><FiEdit2 /></button>
                          <button className="btn btn-ghost btn-icon" title={t('delete')} onClick={() => deleteTx(tx.id)} disabled={!!draft || closed}><FiTrash2 /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {draft && draft.id === null && (
                  <RegisterDraftRow draft={draft} saving={saving} onField={setDraftField} onConfirm={confirmDraft} onCancel={cancelDraft} t={t} />
                )}
              </tbody>
              {transactions.length > 0 && (
                <tfoot>
                  <tr>
                    <td className="gp-foot-label" colSpan={2}>{t('kassa_saldo')}</td>
                    <td className="gp-num gp-paid-col gp-foot-total">$ {money(saldo.usd)}</td>
                    <td className="gp-num gp-paid-col gp-foot-total">SRD {money(saldo.srd)}</td>
                    <td className="gp-num gp-paid-col gp-foot-total">€ {money(saldo.eur)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>

            {closed ? (
              <div className="kassa-locked-note muted"><FiLock style={{ marginRight: '6px' }} /> {t('kassa_locked_note')}</div>
            ) : (
              <button className="btn btn-primary gp-add-btn" onClick={startAdd} disabled={!!draft}>
                <FiPlus style={{ marginRight: '6px' }} /> {t('kassa_add_line')}
              </button>
            )}
          </div>
        ) : (
          <KassaGoldLedger date={date} t={t} locked={closed} onChanged={() => loadDay(date)} onPrint={handlePrint} />
        )}
      </main>

      {showClose && (
        <KassaCloseModal
          date={date}
          saldo={saldo}
          t={t}
          onClose={() => setShowClose(false)}
          onSaved={() => loadDay(date)}
        />
      )}
    </div>
  );
}

/* ---- Register draft row (add / edit -> confirm) ---- */
function RegisterDraftRow({ draft, saving, onField, onConfirm, onCancel, t }) {
  const onKey = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); onConfirm(); }
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };
  return (
    <tr className="gp-draft-row" onKeyDown={onKey}>
      <td>
        <select className="gp-cell" value={draft.type} onChange={(e) => onField('type', e.target.value)}>
          <option value="recebido">{t('kassa_recebido')}</option>
          <option value="pagamento">{t('kassa_pagamento')}</option>
        </select>
      </td>
      <td>
        <input className="gp-cell kassa-desc-cell" type="text" autoFocus
          value={draft.description} onChange={(e) => onField('description', e.target.value)} placeholder={t('description_placeholder')} />
      </td>
      {CURR.map((c) => (
        <td key={c} className="gp-num gp-paid-col">
          <input className="gp-cell" type="number" step="0.01" inputMode="decimal"
            value={editVal(draft[c])} onChange={(e) => onField(c, e.target.value)} placeholder="0.00" />
        </td>
      ))}
      <td className="gp-actions-col">
        <div className="gp-row-actions">
          <button className="btn btn-primary btn-icon" title={t('kassa_confirm')} onClick={onConfirm} disabled={saving}><FiCheck /></button>
          <button className="btn btn-ghost btn-icon" title={t('cancel')} onClick={onCancel} disabled={saving}><FiX /></button>
        </div>
      </td>
    </tr>
  );
}

// Inline balance: three currency chips on one line.
function BalInline({ bal, tone, strong }) {
  return (
    <div className={'gp-bal-inline' + (tone ? ` tone-${tone}` : '')}>
      {CURR.map((c) => (
        <span className="gp-bal-chip" key={c}>
          <span className="gp-bal-code">{c.toUpperCase()}</span>
          <span className={'gp-bal-val' + (strong ? ' gp-gold-strong' : '')}>
            {bal[c] < 0 ? '−' : ''}{money(Math.abs(bal[c]))}
          </span>
        </span>
      ))}
    </div>
  );
}
